// Mock data for NurseBloc landing page

export const heroContent = {
  headline: "Virtual Nurse Support for Families Caring for Loved Ones",
  subheadline: "When caring for a loved one feels overwhelming or confusing, we help you make sense of it all — with nurse-led guidance every step of the way.",
  primaryCta: "Contact Your Care Partner",
  secondaryCta: "How It Works"
};

export const validationStatements = [
  "Managing multiple medications, appointments, and instructions can feel overwhelming — and the stakes are high.",
  "Providers don't always coordinate, and it's hard to know who's tracking the big picture.",
  "You worry about missing something important, making a medication mistake, or waiting too long to act.",
  "You're doing your best — but you're tired, carrying the responsibility, and you shouldn't have to do it alone.",
  "That's where NurseBloc comes in. A real nurse partner to help you navigate care, coordinate next steps, and feel confident in the decisions you're making."
];

export const services = [
  {
    title: "Medication Management",
    description: "Expert guidance organizing complex medication schedules, preventing dangerous interactions, and tracking side effects. We create simple systems to eliminate the 49% error rate caregivers experience."
  },
  {
    title: "Healthcare Navigation",
    description: "Navigate the fragmented healthcare system with confidence. We coordinate between multiple specialists, manage appointments, and ensure all providers communicate - solving the 36% communication barrier caregivers face."
  },
  {
    title: "Medical Translation & Advocacy",
    description: "Transform complex medical language into clear, actionable decisions. We speak up for your loved one in healthcare settings and ensure you understand all options - addressing the information gaps 53% of caregivers experience."
  },
  {
    title: "Care Coordination Planning",
    description: "Create comprehensive care plans that address multiple chronic conditions. We organize home health, equipment, and services while balancing quality of life with treatment needs."
  },
  {
    title: "Emotional Support & Resources",
    description: "Dedicated support for caregiver burnout, guilt, and anxiety. We connect you with resources and provide the emotional backing that prevents caregivers from becoming 'second-order patients'."
  },
  {
    title: "Care Coordination",
    description: "We don't replace your loved one's doctors. We help you navigate the healthcare system, understand your options, and make informed decisions."
  }
];

export const howItWorks = [
  {
    title: "Connect with a Nurse",
    description: "Schedule a virtual call with an experienced nurse who specializes in helping caregivers navigate complex care situations.",
    icon: "Users"
  },
  {
    title: "Get a Clear Plan", 
    description: "We take time to understand your situation, explain your options in plain language, and help you decide what to do next — with confidence.",
    icon: "Clipboard"
  },
  {
    title: "Ongoing Support",
    description: "As needs change, we stay involved. Our nurses help you coordinate care, organize next steps, and address concerns before they turn into emergencies.",
    icon: "Users"
  }
];

export const whyNurse = [
  {
    title: "We support both sides of the care relationship",
    description: "Care works best when caregivers are informed, supported, and included — not left to manage everything behind the scenes. We help caregivers understand what's happening and what matters most, loved ones receive care that fits their real-life needs, and families stay aligned as situations evolve.",
    icon: "Users"
  },
  {
    title: "We look beyond coverage to what's actually needed",
    description: "Insurance-based programs can be helpful, but they don't always address practical challenges. We help the care pair consider medical needs and daily routines, access to services and transportation, and safety, support systems, and caregiver capacity.",
    icon: "MapPin"
  },
  {
    title: "We provide continuity through change",
    description: "Care doesn't stop when eligibility ends or services shift. We help the care pair stay oriented during transitions, understand next options when care plans change, and avoid reactive decisions made under stress.",
    icon: "RefreshCw"
  },
  {
    title: "We help make care more human",
    description: "Our nurses don't replace providers or insurers. We help translate, coordinate, and support — so decisions feel informed, shared, and grounded in what matters to both people.",
    icon: "Heart"
  }
];

export const forYou = [
  "Families managing 5+ medications with complex schedules",
  "Caregivers coordinating between multiple specialists",
  "People dealing with dementia, stroke, or multiple chronic conditions",
  "Anyone experiencing caregiver burnout, guilt, or anxiety"
];

export const notForYou = [
  "Emergency medical care (call 911)",
  "Replacing your loved one's healthcare team",
  "Managing simple, straightforward health situations",
  "Families who don't want ongoing coordination support"
];

export const testimonials = [
  {
    quote: "I was making so many medication mistakes. NurseBloc created a simple system that eliminated my fear and uncertainty.",
    author: "Sarah M.",
    relation: "Daughter managing mom's 8 medications"
  },
  {
    quote: "Her doctors weren't talking to each other. Our nurse coordinated everything and finally got them on the same page.",
    author: "Michael T.",
    relation: "Son coordinating 4 specialists for father"
  },
  {
    quote: "I was burned out and becoming sick myself. The emotional support and practical guidance saved my health too.",
    author: "Jennifer K.",
    relation: "Wife caring for husband with dementia and heart disease"
  }
];

export const images = {
  family: "https://images.unsplash.com/photo-1559827260-dc66d52bef19?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwyfHxlbGRlcmx5JTIwY2FyZSUyMHZpcnR1YWwlMjBjb25zdWx0YXRpb258ZW58MHx8fHwxNzY2ODcxMTYxfDA&ixlib=rb-4.1.0&q=85",
  caring: "https://images.unsplash.com/photo-1584467714173-8bbf314f84d0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwxfHxjYXJlZ2l2ZXIlMjBkaXNjdXNzaW5nJTIwbWVkaWNhdGlvbnxlbnwwfHx8fDE3NjY4NzExNjF8MA&ixlib=rb-4.1.0&q=85",
  nurse: "https://images.unsplash.com/photo-1576091160550-2173dba999ef?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwxfHx2aWRlbyUyMGNhbGwlMjBkb2N0b3J8ZW58MHx8fHwxNzY2ODcxMTYxfDA&ixlib=rb-4.1.0&q=85",
  caregiver: "https://images.unsplash.com/photo-1587854692152-cbe660dbde88?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwzfHxjYXJlZ2l2ZXIlMjBoZWxwaW5nJTIwc2VuaW9yJTIwd2l0aCUyMG1lZGljYXRpb25zfGVufDB8fHx8MTc2Njg3MTE2MXww&ixlib=rb-4.1.0&q=85",
  virtualConsultation: "https://images.unsplash.com/photo-1576091160550-2173dba999ef?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwxfHx2aWRlbyUyMGNhbGwlMjBkb2N0b3J8ZW58MHx8fHwxNzY2ODcxMTYxfDA&ixlib=rb-4.1.0&q=85",
  medicationManagement: "https://images.unsplash.com/photo-1584308666744-a24ff540585b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwxfHxtZWRpY2F0aW9uJTIwbWFuYWdlbWVudHxlbnwwfHx8fDE3NjY4NzExNjF8MA&ixlib=rb-4.1.0&q=85",
  careCoordination: "https://images.unsplash.com/photo-1559757148-5c350d0d3c56?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHw0fHxudXJzZSUyMGhlbHBpbmclMjBmYW1pbHl8ZW58MHx8fHwxNzY2ODcxMTYxfDA&ixlib=rb-4.1.0&q=85",
  familySupport: "https://images.unsplash.com/photo-1559827260-dc66d52bef19?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwyfHxlbGRlcmx5JTIwY2FyZSUyMHZpcnR1YWwlMjBjb25zdWx0YXRpb258ZW58MHx8fHwxNzY2ODcxMTYxfDA&ixlib=rb-4.1.0&q=85",
  healthcareNavigation: "https://images.unsplash.com/photo-1576091160550-2173dba999ef?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwxfHx2aWRlbyUyMGNhbGwlMjBkb2N0b3J8ZW58MHx8fHwxNzY2ODcxMTYxfDA&ixlib=rb-4.1.0&q=85",
  patientAdvocacy: "/screenshot.png",
  chronicCare: "https://images.unsplash.com/photo-1559827260-dc66d52bef19?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwyfHxlbGRlcmx5JTIwY2FyZSUyMHZpcnR1YWwlMjBjb25zdWx0YXRpb258ZW58MHx8fHwxNzY2ODcxMTYxfDA&ixlib=rb-4.1.0&q=85"
};
